/**
 * Central definitions for lift status sensors.
 *
 * `SENSOR` maps every known sensor to a human readable name so the codebase can
 * refer to `SENSOR.RearDoorPosition` instead of the magic number `503`.
 *
 * The value maps (`HealthStatus`, `DoorPosition`, `MovementState`) together with
 * the type guards below replace the scattered `Number()`/`String()` casts and the
 * hard coded comparisons against `1`, `0`, `"1"`, `"0"`, `"U"`, `"C"`, etc.
 */

/**
 * Named sensor IDs. Values are the raw numeric IDs used by the API.
 * Names are derived from the English sensor labels.
 */
export const SENSOR = {
  /** Sense module connected to SenseCloud (1 = connected) */
  SensecloudConnectionHealthy: 10,
  /** Mobile signal strength (percentage) */
  MobileSignalStrength: 23,
  /** SenseCloud Round Trip Time - past hour (ms) */
  SensecloudRttLastHour: 25,
  /** SenseCloud Round Trip Time - past 24 hours (ms) */
  SensecloudRttLast24Hours: 26,
  /** Controller connection with Sense module healthy (1 = healthy) */
  ControllerSenseModuleConnectionHealthy: 50,
  /** Energy meter 3-phase connection healthy (1 = healthy) */
  EnergyMeter3Phase: 54,
  /** Energy meter 1-phase connection healthy (1 = healthy) */
  EnergyMeter1Phase: 55,
  /** Trip counter */
  TripCounter: 100,
  /** Installation unavailable (1 = unavailable) */
  InstallationUnavailable: 103,
  /** Visit */
  Visit: 104,
  /** Available */
  Available: 105,
  /** Elevator in fault */
  ElevatorInFault: 106,
  /** Aggregated emergency stop */
  EmergencyStopAggregated: 107,
  /** Installation power */
  InstallationPower: 108,
  /** Energy consumption counter */
  EnergyConsumptionCounter: 109,
  /** Status reset contact */
  StatusResetContact: 110,
  /** Fault contact */
  FaultContact: 201,
  /** Aggregated fault (with availability impact) */
  AggregatedFault: 210,
  /** Aggregated message (without availability impact) */
  AggregatedMessage: 211,
  /** Technically available */
  TechnicallyAvailable: 212,
  /** Movement state */
  MovementState: 500,
  /** Current stop (doors open) */
  CurrentStopDoorsOpen: 501,
  /** Front door position (status) */
  FrontDoorPosition: 502,
  /** Rear door position (status) */
  RearDoorPosition: 503,
  /** Door line */
  DoorLine: 505,
  /** Temperature controller (-100 to 200) */
  TemperatureController: 506,
  /** Direction change counter */
  DirectionChangeCounter: 507,
  /** Door cycle counter - front door */
  DoorCycleCounterFront: 508,
  /** Door cycle counter - rear door */
  DoorCycleCounterRear: 509,
  /** Door cycle counter per stop - front door */
  DoorCycleCounterPerStopFront: 510,
  /** Door cycle counter per stop - rear door */
  DoorCycleCounterPerStopRear: 511,
  /** Stop counter */
  StopCounter: 512,
  /** Status messages */
  StatusMessages: 513,
  /** Last passed stop */
  LastPassedStop: 514,
  /** Current stop */
  CurrentStop: 515,
  /** Door movement counter - front door */
  DoorMovementCounterFront: 516,
  /** Door movement counter - rear door */
  DoorMovementCounterRear: 517,
  /** Door movement counter per stop - front door */
  DoorMovementCounterPerStopFront: 518,
  /** Door movement counter per stop - rear door */
  DoorMovementCounterPerStopRear: 519,
  /** Door reversal counter - front door */
  DoorReversalCounterFront: 520,
  /** Door reversal counter - rear door */
  DoorReversalCounterRear: 521,
  /** Door reversal counter per stop - front door */
  DoorReversalCounterPerStopFront: 522,
  /** Door reversal counter per stop - rear door */
  DoorReversalCounterPerStopRear: 523,
  /** Controller connection healthy (1 = healthy) */
  ControllerConnectionHealthy: 524,
  /** No response after call (carcall request no response) */
  NoResponseAfterCall: 550,
  /** Controller info */
  ControllerInfo: 590,
  /** Maximum number of floors/stops the lift serves (value is zero-based) */
  MaxFloors: 591,
  /** Lift plugin: name of the loaded plugin, equal to the prefix of the S513 codes produced by the plugin */
  LiftPlugin: 592,
} as const;

export type SENSOR = (typeof SENSOR)[keyof typeof SENSOR];

/**
 * Health status of a "connection healthy" style sensor (e.g. 10, 54, 55, 524).
 * The raw value is `1` (healthy), `0` (unhealthy) or `null`/`undefined`/empty
 * (the sensor never reported a value, i.e. not connected).
 */
export const HealthStatus = {
  NotConnected: "NOT_CONNECTED",
  Healthy: "HEALTHY",
  Unhealthy: "UNHEALTHY",
} as const;

export type HealthStatus = (typeof HealthStatus)[keyof typeof HealthStatus];

/**
 * Door position values as reported by sensors 502 and 503.
 */
export const DoorPosition = {
  Unknown: "",
  Error: "?",
  Closed: "C",
  Open: "O",
  Closing: "-",
  Opening: "+",
  Locked: "X",
} as const;

export type DoorPosition = (typeof DoorPosition)[keyof typeof DoorPosition];

/**
 * Movement state values as reported by sensor 500.
 */
export const MovementState = {
  Unknown: "",
  Error: "?",
  Inactive: "I",
  Up: "U",
  Down: "D",
} as const;

export type MovementState = (typeof MovementState)[keyof typeof MovementState];

/**
 * Sensors that report a binary "connection healthy" value (1 = healthy).
 */
export const HEALTH_SENSORS = [
  SENSOR.SensecloudConnectionHealthy,
  SENSOR.ControllerSenseModuleConnectionHealthy,
  SENSOR.EnergyMeter3Phase,
  SENSOR.EnergyMeter1Phase,
  SENSOR.ControllerConnectionHealthy,
];

/**
 * "Normal check" sensors where 1 is the good/green state.
 */
export const NORMAL_CHECK_SENSORS = [
  SENSOR.Visit,
  SENSOR.SensecloudConnectionHealthy,
  SENSOR.ControllerConnectionHealthy,
];

/**
 * "Inverted check" sensors where 1 is the bad/red state.
 */
export const INVERTED_CHECK_SENSORS = [
  SENSOR.Available,
  SENSOR.TechnicallyAvailable,
  SENSOR.NoResponseAfterCall,
];

/**
 * Energy meter connection-health sensors.
 */
export const ENERGY_METER_SENSORS = [
  SENSOR.EnergyMeter3Phase,
  SENSOR.EnergyMeter1Phase,
];

/**
 * Sensors whose value is zero-based and needs +1 for display.
 */
export const ZERO_BASED_STOP_SENSORS = [
  SENSOR.CurrentStopDoorsOpen,
  SENSOR.LastPassedStop,
  SENSOR.CurrentStop,
];

/**
 * Sensors the 3D scene reads to drive its movement / stop / door display state.
 */
export const SCENE_SENSOR_IDS = [
  SENSOR.MovementState,
  SENSOR.FrontDoorPosition,
  SENSOR.RearDoorPosition,
  SENSOR.LastPassedStop,
  SENSOR.CurrentStop,
];

/**
 * Raw values of a binary "connection healthy" sensor (e.g. 10, 54, 55, 524).
 */
export const HealthSensorValue = {
  Unhealthy: 0,
  Healthy: 1,
} as const;

export type HealthSensorValue =
  (typeof HealthSensorValue)[keyof typeof HealthSensorValue];

/**
 * Default sensors shown by the swimlane view.
 */
export const SWIMLANE_SENSOR_IDS: number[] = [
  SENSOR.MovementState,
  SENSOR.CurrentStopDoorsOpen,
  SENSOR.FrontDoorPosition,
  SENSOR.RearDoorPosition,
  SENSOR.LastPassedStop,
  SENSOR.CurrentStop,
];

/**
 * Default sensors shown by the status grid (swimlane sensors + controller info).
 */
export const STATUS_SENSOR_IDS: number[] = [
  ...SWIMLANE_SENSOR_IDS,
  SENSOR.ControllerInfo,
];

/**
 * All sensors requested from the API/socket by `ls-data`.
 */
export const DATA_REQUEST_SENSOR_IDS: number[] = [
  SENSOR.SensecloudConnectionHealthy,
  SENSOR.MobileSignalStrength,
  SENSOR.EnergyMeter3Phase,
  SENSOR.EnergyMeter1Phase,
  SENSOR.Visit,
  SENSOR.Available,
  SENSOR.ElevatorInFault,
  SENSOR.AggregatedFault,
  SENSOR.TechnicallyAvailable,
  SENSOR.MovementState,
  SENSOR.CurrentStopDoorsOpen,
  SENSOR.FrontDoorPosition,
  SENSOR.RearDoorPosition,
  SENSOR.StatusMessages,
  SENSOR.LastPassedStop,
  SENSOR.CurrentStop,
  SENSOR.ControllerConnectionHealthy,
  SENSOR.NoResponseAfterCall,
  SENSOR.ControllerInfo,
  SENSOR.MaxFloors,
  SENSOR.LiftPlugin,
];

/** Serialize a list of sensor IDs to the comma-separated string props expect. */
export const sensorIdsToString = (ids: readonly number[]): string =>
  ids.join(",");

/**
 * Raw sensor values that must be substituted for display purposes.
 * The inverted question mark `"¿"` is shown as a regular `"?"` because the
 * font does not support the upside-down glyph.
 */
export const SENSOR_DISPLAY_VALUE_OVERRIDES: Record<string, string> = {
  "¿": "?",
};

/** Apply display-only substitutions to a raw sensor value. */
export const getDisplaySensorValue = <T>(value: T): T | string =>
  typeof value === "string" && value in SENSOR_DISPLAY_VALUE_OVERRIDES
    ? SENSOR_DISPLAY_VALUE_OVERRIDES[value]
    : value;

/**
 * Whether `sensorId` is one of the given sensors. The rest parameter widens the
 * literal `SENSOR.*` values, so both inline lists and spread arrays work without
 * a cast or annotation:
 *   `isSensorId(id, SENSOR.EnergyMeter3Phase, SENSOR.EnergyMeter1Phase)`
 *   `isSensorId(id, ...HEALTH_SENSORS)`
 */
export const isSensorId = (sensorId: number, ...sensors: number[]): boolean =>
  sensors.includes(sensorId);

/** Whether the sensor reports a binary "connection healthy" value. */
export const isHealthSensor = (sensorId: number): boolean =>
  isSensorId(sensorId, ...HEALTH_SENSORS);

/** Whether the sensor is a "normal check" sensor (1 = good). */
export const isNormalCheckSensor = (sensorId: number): boolean =>
  isSensorId(sensorId, ...NORMAL_CHECK_SENSORS);

/** Whether the sensor is an "inverted check" sensor (1 = bad). */
export const isInvertedCheckSensor = (sensorId: number): boolean =>
  isSensorId(sensorId, ...INVERTED_CHECK_SENSORS);

/** Whether the sensor value is zero-based and needs +1 for display. */
export const isZeroBasedStopSensor = (sensorId: number): boolean =>
  isSensorId(sensorId, ...ZERO_BASED_STOP_SENSORS);

/**
 * Derive the {@link HealthStatus} from a raw "connection healthy" sensor value.
 * `null`/`undefined`/empty means the sensor never reported (not connected),
 * `1` means healthy and anything else means unhealthy.
 */
export const getHealthStatus = (
  value: string | number | boolean | null | undefined,
): HealthStatus => {
  if (value === null || value === undefined || value === "") {
    return HealthStatus.NotConnected;
  }

  const numeric = typeof value === "number" ? value : Number(value);

  if (Number.isNaN(numeric)) {
    return HealthStatus.NotConnected;
  }

  return numeric === HealthSensorValue.Healthy
    ? HealthStatus.Healthy
    : HealthStatus.Unhealthy;
};

/** Type guard: whether the value is a known {@link DoorPosition}. */
export const isDoorPosition = (
  value: string | number | null | undefined,
): value is DoorPosition =>
  typeof value === "string" &&
  (Object.values(DoorPosition) as string[]).includes(value);

/** Type guard: whether the value is a known {@link MovementState}. */
export const isMovementState = (
  value: string | number | null | undefined,
): value is MovementState =>
  typeof value === "string" &&
  (Object.values(MovementState) as string[]).includes(value);
